/**
 * The models a document form's SOURCES cover, loaded once for whoever asks.
 *
 * Both the Body tab's placement panel and the Fields tab's grouping need the
 * SAME thing: every covered transaction's forms and columns, by the same names.
 * It used to live inside `BodyTab`, which meant the Fields tab could only have
 * it by fetching the models a second time — two caches of one fact, and the
 * ordinary way the two surfaces start disagreeing about what a form is called.
 *
 * Nothing here is derived: the shaping is `sources.buildCatalog`, which is
 * still the one place that reads a transaction model.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { loadTransactionObject, loadVisualObject } from '../../../api/designers'
import {
  buildCatalog, formsOfModel, type SourceForm, type SourceTxn,
} from './sources'
import type { TxnRow } from './types'

export interface TxnCatalog {
  /** One entry per transaction the form covers, in the order it declares them. */
  catalog: SourceTxn[]
  /** Every model fetched, by UPPER obj_name — including ones that failed. */
  models: Record<string, unknown>
  /** The forms of every model fetched, for objects reached in one hop. */
  farForms: Record<string, SourceForm[]>
  /** True while at least one fetch is in flight. */
  loading: boolean
  /** Fetch a model not named by `transactions[]` — a lookup's far object. */
  loadObject: (name: string, kind?: string) => void
}

export function useTxnCatalog(project: string, txns: TxnRow[]): TxnCatalog {
  const [models, setModels] = useState<Record<string, unknown>>({})
  const [loading, setLoading] = useState(false)
  const wanted = useRef<Set<string>>(new Set())

  const loadObject = useCallback((rawName: string, kind?: string) => {
    const name = String(rawName || '').trim()
    if (!name) return
    const upper = name.toUpperCase()
    // Keyed by KIND as well as name. A visual and a transaction may share a
    // name — this feature makes that the normal case, since a Document visual
    // and its document form are deliberately named the same — and a name-only
    // key loaded whichever came first, then ran the wrong reader over it and
    // produced an empty column list.
    const cacheKey = `${kind === 'visual' ? 'V' : 'T'}:${upper}`
    if (wanted.current.has(cacheKey)) return
    wanted.current.add(cacheKey)
    setLoading(true)
    // WHICH loader is decided by the declaration, not guessed from the name.
    // A visual and a transaction may perfectly well share one — they are
    // different object types, and the model files live in different folders.
    const fetching = kind === 'visual'
      ? loadVisualObject(project, name)
      : loadTransactionObject(project, name.toLowerCase())
    fetching
      // `.content` is the model; the response is an envelope. Unwrapped here
      // AND tolerated in `formsOfModel`, because this was the bug that made
      // the whole panel render empty.
      .then((got) => setModels((prev) => ({
        ...prev,
        [cacheKey]: (got as { content?: unknown })?.content ?? got,
      })))
      // A model that will not load is not a crash: the group shows nothing and
      // everything else on the screen keeps working. It is also the ordinary
      // case for an object the identity may not read.
      .catch(() => setModels((prev) => ({ ...prev, [cacheKey]: null })))
      .finally(() => setLoading(false))
  }, [project])

  useEffect(() => {
    (txns || []).forEach((t) => loadObject(t.obj_name, t.kind))
  }, [txns, loadObject])

  const catalog = useMemo(
    () => buildCatalog(txns || [], models), [txns, models])

  const farForms = useMemo(() => {
    const out: Record<string, SourceForm[]> = {}
    for (const [cacheKey, raw] of Object.entries(models)) {
      // TRANSACTIONS only: `farForms` serves a lookup's far object, and a
      // lookup always names a transaction. Reading a visual's model with
      // `formsOfModel` would find no `sql_models` and yield an empty entry
      // that looks like a broken transaction.
      if (!raw || cacheKey.startsWith('V:')) continue
      out[cacheKey.replace(/^T:/, '')] = formsOfModel(raw)
    }
    return out
  }, [models])

  return { catalog, models, farForms, loading, loadObject }
}
